import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";
import { useCenterSubscription } from "../../../subscription";
import { moduleName } from "../../../marketplace";
import { MeetingBackgroundShelf } from "../../../session";
import { useCenter, useCenterToolPolicy, useSetToolPolicy } from "../../../center";
import type { CenterToolPolicy } from "../../../center";
import CenterAdminShell from "../components/CenterAdminShell";

/** An amount in minor units, in the reader's language. */
const money = (cents: number, language: string): string =>
    new Intl.NumberFormat(language, { style: "currency", currency: "EUR" })
        .format(cents / 100);

/**
 * Whether the centre has already paid for this module's current period.
 *
 * What makes switching it on free. A date in the past is a period that has
 * run out — switching the module on then is a fresh purchase, and it is
 * charged like one.
 */
const paidFor = (policy: { paid_until?: string | null }): boolean => {
    if (!policy.paid_until) return false;
    const until = new Date(policy.paid_until);
    return !Number.isNaN(until.getTime()) && until.getTime() > Date.now();
};

const formatDay = (raw: string, language: string): string => {
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return "";
    return new Intl.DateTimeFormat(language, { dateStyle: "long" }).format(parsed);
};

/**
 * Which tools the centre lets its professionals use, and what they can put
 * behind themselves in a call.
 *
 * Switching one off never deletes anybody's adoption — the tool goes inert and
 * comes back exactly as it was. The count beside each one says how many people
 * the decision affects, which is the number worth knowing before taking it.
 *
 * A **paid** module does not go inert at once. The centre paid for it up to the
 * renewal and money is never given back, so it stays on for the members until
 * then and the row says which day it goes. Switching it back on before that
 * cancels the whole thing.
 *
 * The centre's meeting backgrounds are here rather than only in the room. An
 * administrator filling the library is doing administration, not holding a
 * session, and making them join a call to upload a wall would be asking them to
 * open a video room to manage a folder. The therapists then find the pictures
 * waiting in their own picker.
 */
export default function CenterToolsPage() {
    const { t, i18n } = useTranslation();
    const { center, loading } = useCenter();
    const { policies, loading: policyLoading } = useCenterToolPolicy(center?.id);
    const setPolicy = useSetToolPolicy(center?.id);
    const [error, setError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState<CenterToolPolicy | null>(null);
    const { data: subscription } = useCenterSubscription(center?.id);
    const seats = subscription?.seats ?? 0;
    const language = i18n.language || "es";
    // The period the centre is billed in: a module joins the subscription it
    // already has, so a yearly centre pays the yearly price per seat.
    const period = subscription?.subscription?.billingPeriod ?? "monthly";
    const priceOf = (policy: CenterToolPolicy | null): number => {
        const monthly = policy?.monthly_price_cents ?? 0;
        if (period !== "annual") return monthly;
        return policy?.annual_price_cents || monthly;
    };
    // True only when there is a real annual price to quote. An older server
    // sends none, and a yearly figure worked out here would match no invoice.
    const yearly = period === "annual" && (confirming?.annual_price_cents ?? 0) > 0;

    const toggle = async (toolId: string, enabled: boolean) => {
        setError(null);
        try {
            await setPolicy.mutateAsync({ toolId, state: enabled ? "enabled" : "disabled" });
        } catch {
            setError(t("center.tools.errorGeneric"));
        }
    };

    /**
     * Switching a **paid** module on adds it to the centre's bill, across
     * every seat it has bought. Asked first, with the figure: an
     * administrator pressing a button should not find out what it cost from
     * the next invoice.
     *
     * Switching one off is not asked about. It costs nothing, and the members
     * keep it to the renewal either way — the row already says which day it
     * goes.
     */
    const press = (policy: CenterToolPolicy, enabling: boolean) => {
        const price = policy.monthly_price_cents ?? 0;
        // Paid for already, and the period has not run out: switching it on
        // takes no money, so it takes no confirmation either. Asking "this
        // will cost 5,00 € per seat — are you sure?" when the answer is that
        // it costs nothing would be the screen lying to be safe.
        if (enabling && price > 0 && !paidFor(policy)) {
            setConfirming(policy);
            return;
        }
        void toggle(policy.tool_id, enabling);
    };

    if (loading || !center) {
        return <p className="p-6 text-sm text-gray-500">{t("common.loading")}</p>;
    }

    return (
        <CenterAdminShell center={center}>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h2 className="mb-1 text-lg font-semibold text-content-primary">
                    {t("center.tools.title")}
                </h2>
                <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                    {t("center.tools.subtitle")}
                </p>

                {error && (
                    <p role="alert" className="mb-4 text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}

                {policyLoading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

                {!policyLoading && policies.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t("center.tools.empty")}
                    </p>
                )}

                <ul className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
                    {policies.map((policy) => (
                        <li
                            key={policy.tool_id}
                            className="flex flex-wrap items-center gap-3 py-3 text-sm"
                        >
                            {/* The catalogue stores names in English; the
                                therapist catalogue's copy translates them. */}
                            <span className="text-content-primary">{moduleName(t, policy)}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {t("center.tools.adoptedBy", { count: policy.adopted_by })}
                            </span>
                            {/* Switched off, and paid for until a date: the
                                centre can switch it on again for nothing up
                                to then, which is the thing worth saying. */}
                            {paidFor(policy) && (
                                <span className="text-xs text-amber-700 dark:text-amber-400">
                                    {t("center.tools.paidUntil", {
                                        date: formatDay(
                                            policy.paid_until as string,
                                            i18n.language || "es"
                                        ),
                                    })}
                                </span>
                            )}
                            <span className="ml-auto flex items-center gap-3">
                                <span
                                    className={`rounded-full px-2 py-0.5 text-xs ${
                                        policy.state === "enabled"
                                            ? "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400"
                                            : "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400"
                                    }`}
                                >
                                    {t(`center.tools.states.${policy.state}`)}
                                </span>
                                {/* Switching one on costs nothing while the
                                    period it was paid for is still running:
                                    those days were charged once and credited
                                    nothing when it went off, so there is no
                                    second bill and nothing to confirm. */}
                                <Button
                                    size="sm"
                                    variant={
                                        policy.state === "enabled" ? "outline" : "primary"
                                    }
                                    onClick={() => press(policy, policy.state !== "enabled")}
                                    disabled={setPolicy.isPending}
                                >
                                    {policy.state === "enabled"
                                        ? t("center.tools.disable")
                                        : t("center.tools.enable")}
                                </Button>
                            </span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* What it costs, before it is switched on. */}
            <ConfirmDialog
                isOpen={confirming !== null}
                title={t("center.tools.paidConfirm.title", {
                    tool: confirming ? moduleName(t, confirming) : "",
                })}
                message={t(
                    yearly
                        ? "center.tools.paidConfirm.messageAnnual"
                        : "center.tools.paidConfirm.message",
                    {
                        price: money(priceOf(confirming), language),
                        seats,
                        total: money(priceOf(confirming) * seats, language),
                    }
                )}
                // Before VAT, like every price the centre is quoted.
                details={
                    <p className="text-caption text-content-tertiary">
                        {t("subscription.billing.tax.note")}
                    </p>
                }
                confirmLabel={t("center.tools.paidConfirm.confirm")}
                isBusy={setPolicy.isPending}
                onConfirm={() => {
                    if (confirming) void toggle(confirming.tool_id, true);
                    setConfirming(null);
                }}
                onCancel={() => setConfirming(null)}
            />

            <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <h2 className="mb-1 text-lg font-semibold text-content-primary">
                    {t("center.backgrounds.title")}
                </h2>
                <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                    {t("center.backgrounds.subtitle")}
                </p>

                {/* The same shelf the room shows, in the place an
                    administrator already works. */}
                <MeetingBackgroundShelf scope="center" heading={false} />
            </section>
        </CenterAdminShell>
    );
}
